import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCategoryScore } from "../../api/categoryService";
import { joinGame, resolveWebSocketUrl } from "../../api/gameService";
import { useAuth } from "../../context/AuthContext";
import { useAvatar } from "../../context/AvatarContext";
import { useGameCtx } from "../../context/GameContext";
import { ensureGuestId, getGuestPseudo } from "../../utils/localStorage";
import Avatar from "../ui/Avatar";

export default function ReadyToPlayModal({ isOpen, onClose }) {
  const { selectedCategory, player } = useGameCtx();
  const { user, isAuthenticated } = useAuth();
  const { currentAvatar } = useAvatar();
  const [scoreInfo, setScoreInfo] = useState(null);
  const navigate = useNavigate();
  const authenticatedUserId = user?.user_id || user?.id || null;

  useEffect(() => {
    if (!isOpen || !selectedCategory) return;
    const ctrl = new AbortController();

    fetchCategoryScore(
      {
        slug: selectedCategory.slug,
        userId: isAuthenticated ? authenticatedUserId : player?.id,
      },
      ctrl.signal
    )
      .then(setScoreInfo)
      .catch(() => setScoreInfo(null));

    return () => ctrl.abort();
  }, [isOpen, selectedCategory, player, authenticatedUserId, isAuthenticated]);

  if (!isOpen) return null;

  const displayedPseudo = isAuthenticated
    ? user?.username
    : player?.pseudo || getGuestPseudo() || "Invite";

  const handleJoin = async () => {
    try {
      const data = await joinGame(selectedCategory.slug);

      if (!data.ws_url) {
        alert("Erreur : aucune URL WebSocket recue.");
        return;
      }

      const finalWsUrl = resolveWebSocketUrl(data.ws_url);

      navigate(`/game-quiz/${selectedCategory.slug}`, {
        state: {
          wsUrl: finalWsUrl,
          category: selectedCategory,
          player: {
            player_id: isAuthenticated ? authenticatedUserId : player?.id,
            pseudo: displayedPseudo,
            avatar_id: currentAvatar?.id || null,
            guest_id: isAuthenticated ? null : player?.guest_id || ensureGuestId(),
          },
        },
      });
    } catch (error) {
      console.error(error);
      alert("Impossible de rejoindre la partie. Reessayez.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="relative w-[90%] max-w-md rounded-2xl bg-white p-8 text-center shadow-xl animate-fade-in">
        <h3 className="mb-6 text-2xl font-bold text-gray-800">
          Prêt à jouer ?
        </h3>

        <div className="mb-5 flex justify-center">
          <Avatar src={currentAvatar?.image || null} name={displayedPseudo} size="lg" />
        </div>

        <div className="mb-8 space-y-3 text-lg text-gray-700">
          <p>
            <span className="font-medium text-gray-600">Pseudo :</span>{" "}
            <b>{displayedPseudo}</b>
          </p>

          <p>
            <span className="font-medium text-gray-600">Catégorie :</span>{" "}
            <b>{selectedCategory?.name}</b>
          </p>

          <p>
            <span className="font-medium text-gray-600">Score global :</span>{" "}
            <b>{scoreInfo?.score ?? 0}</b>{" "}
            {scoreInfo?.rank ? (
              <span className="text-sm text-gray-500">(Rang {scoreInfo.rank})</span>
            ) : null}
          </p>
        </div>

        <div className="flex justify-center gap-4">
          <button
            onClick={onClose}
            className="rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-800 transition hover:bg-gray-300"
          >
            Changer
          </button>

          <button
            onClick={handleJoin}
            className="rounded-lg bg-pink-600 px-5 py-2 font-semibold text-white transition hover:bg-pink-700"
          >
            Rejoindre la partie
          </button>
        </div>
      </div>
    </div>
  );
}
